import { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { AlertCircle, CheckCircle, Database, Search } from 'lucide-react';
import { projectId } from '../utils/supabase/info';

interface CompanyDataDiagnosticProps {
  accessToken: string;
}

export function CompanyDataDiagnostic({ accessToken }: CompanyDataDiagnosticProps) {
  const [companyId, setCompanyId] = useState('79fe648e-866f-4c0e-86da-f60894033c57');
  const [diagnosticData, setDiagnosticData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runDiagnostic = async () => {
    setLoading(true);
    setError(null);
    setDiagnosticData(null);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/diagnostic/company-data/${companyId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to fetch diagnostic data');
      }

      const data = await response.json();
      setDiagnosticData(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Company Data Diagnostic Tool
          </CardTitle>
          <CardDescription>
            Check the raw data stored for a specific company
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="companyId">Company ID</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="companyId"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                placeholder="Enter company ID"
              />
              <Button onClick={runDiagnostic} disabled={loading || !companyId}>
                <Search className="h-4 w-4 mr-2" />
                {loading ? 'Checking...' : 'Run Diagnostic'}
              </Button>
            </div>
          </div>

          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {diagnosticData && (
            <div className="space-y-4">
              <Alert className={diagnosticData.hasData ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}>
                <CheckCircle className={`h-4 w-4 ${diagnosticData.hasData ? 'text-green-600' : 'text-yellow-600'}`} />
                <AlertDescription className={diagnosticData.hasData ? 'text-green-800' : 'text-yellow-800'}>
                  {diagnosticData.hasData ? 'Company data found in database' : 'No company data found'}
                </AlertDescription>
              </Alert>

              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-medium">Summary</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Company ID:</span>
                    <p className="font-mono text-xs break-all">{diagnosticData.companyId}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Has Data:</span>
                    <p>{diagnosticData.hasData ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Projects Count:</span>
                    <p>{diagnosticData.projectsCount}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">SOWs Count:</span>
                    <p>{diagnosticData.sowsCount}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Discovery Override:</span>
                    <p>{diagnosticData.discoveryOverride ?? 'None'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Implementation Override:</span>
                    <p>{diagnosticData.implementationOverride ?? 'None'}</p>
                  </div>
                </div>
              </div>

              {diagnosticData.dataKeys && diagnosticData.dataKeys.length > 0 && (
                <div className="border rounded-lg p-4 space-y-2">
                  <h3 className="font-medium">Data Keys Present</h3>
                  <div className="flex flex-wrap gap-2">
                    {diagnosticData.dataKeys.map((key: string) => (
                      <code key={key} className="px-2 py-1 bg-muted rounded text-xs">
                        {key}
                      </code>
                    ))}
                  </div>
                </div>
              )}

              {diagnosticData.companyInfo && (
                <div className="border rounded-lg p-4 space-y-2">
                  <h3 className="font-medium">Company Info</h3>
                  <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-40">
                    {JSON.stringify(diagnosticData.companyInfo, null, 2)}
                  </pre>
                </div>
              )}

              {diagnosticData.companyData && (
                <div className="border rounded-lg p-4 space-y-2">
                  <h3 className="font-medium">Full Company Data</h3>
                  <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-96">
                    {JSON.stringify(diagnosticData.companyData, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
